import { SetStateAction, useState, useEffect } from "react";
import styled from "styled-components";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { useModalContext } from "components/modal";
import { Button, OutlineButton } from "components/button";
import Dropdown from "components/dropdown";
import ConfirmSendModal from "./ConfirmSendModal";
import { RiFileTextLine } from "react-icons/ri";
import { FaCheck } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useSnackbarContext } from "components/snackbar";
import { IProperty, ICustomerPlan } from "../interface";
import ToggleSwitch from "components/toggleSwitch";
import DatePicker from "react-datepicker";
import { enUS, es } from "date-fns/locale";

interface IProps {
  customerId: string;
  customerEmail: string;
  customerPlans: ICustomerPlan[];
};

const AddPlanModal = ({ customerId, customerEmail, customerPlans }: IProps) => {
  const { t, i18n } = useTranslation();
  const tBase = "views.service-plans.components.AddPlanModal";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const locale = i18n.language === "es" ? es : enUS;
  const { setModal } = useModalContext();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbarContext();

  const [servicePlanId, setServicePlanId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [hasContract, setHasContract] = useState(false);
  const [dateOption, setDateOption] = useState("immediately");
  const [startDate, setStartDate] = useState(new Date().toISOString());
  
  const plansData = useQuery(
    "plans",
    async () => 
      await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/customers?type=PLANS`
        )
        .then((res) => {
          // return array of plans sorted alphabetically by name
          return res.data?.sort((a: { name: string; }, b: { name: string; }) => {
            const nameA = a.name?.toLowerCase() || "";
            const nameB = b.name?.toLowerCase() || "";
            return nameA.localeCompare(nameB);
          })
        })
  );

  const activePlans = plansData.data?.filter((p: { archived: boolean; }) => !p.archived) || [];

  useEffect(() => {
    if (!servicePlanId || !plansData.data) return;
  
    const selectedPlan = plansData.data.find(
      (x: { id: { toString: () => string } }) =>
        x.id?.toString() === servicePlanId.toString()
    );

    setDateOption(
      selectedPlan?.start_date_type || "immediately"
    );
  
    setHasContract(!!selectedPlan?.is_contract);
  }, [servicePlanId, plansData.data]);

  const properties = useQuery(
    ["properties", customerId, "properties"],
    async () =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/customers/${customerId}/property`,
          { params: { id: customerId } }
        )
        .then((res) =>
          res.data?.map((x: any) => {
            return x;
          })
        )
  );

  const addPlanToCustomer = useMutation(
    async (payload: { 
      plans: [{
        servicePlanId: string; 
        customerId: string; 
        propertyId: string;
        startDate: string;
      }]
    }) => {
      const res = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/add-plans/add`,
        payload 
      );
      return res.data; 
    },
    {
      onSuccess: () => {
        showSnackbar(tr("Plan added successfully"));
        queryClient.invalidateQueries(["customerPlans", customerId, "plans"]);
        queryClient.invalidateQueries("plans");
        queryClient.invalidateQueries(["customer", customerId]);
      },
      onError: () => {
        console.error("Error adding plan to customer");
        showSnackbar(tr("An error occurred while adding the plan"));
      },
    }
  );

  const isPlanPropertyExist = (planId: string) => {
    return !!propertyId &&
    customerPlans.some(
      (p) =>
        p.service_planid === planId.toString() &&
        p.fk_bcus_address === propertyId.toString()
    );
  };

  const isPropertyPlanExist = (propertyId: string) => {
    return !!servicePlanId &&
    customerPlans.some(
      (p) => 
        p.service_planid === servicePlanId.toString() &&
        p.fk_bcus_address === propertyId.toString()
    );
  };

  return (
    <div className="px-3">
      {/* Service Plan Id Dropdown */}
      <div className="mb-3">
        <div className="text-gray-700 mb-0.5">
          {tr("Select a plan to add to the customer")}
        </div>
        <Dropdown
          reference={
            <button className="h-10 w-full bg-white border border-gray-300 rounded-md shadow-sm text-gray-700 text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 px-2">
              {servicePlanId ? (
                <div className="relative">
                  <div className="flex flex-row justify-center items-center space-x-1">
                    <div>
                      {
                        activePlans?.find(
                          (x: { id: { toString: () => string } }) =>
                            x.id?.toString() === servicePlanId?.toString()
                        )?.name
                      }
                    </div>
                    <div>
                      {hasContract && (
                        <RiFileTextLine size={22} className="text-primary" />
                      )}
                    </div>
                  </div>
                  <div className="absolute top-[-20px] right-[-20px] bg-white rounded-full border border-gray-300 p-0.5">
                    <FaCheck size={18} className="text-green-500" />
                  </div>
                </div>
              ) : (
                tr("Select Plan")
              )}
            </button>
          }
          data={[
            {
              button: activePlans?.map(
                (x: {
                  is_contract: boolean;
                  name: string;
                  id: SetStateAction<string | number | undefined>;
                }) => {
                  const exists = isPlanPropertyExist(String(x.id));

                  if (x.is_contract === true) {
                    return {
                      label: (
                        <div className="flex flex-row justify-between items-center space-x-1">
                          <div>{x.name}</div>
                          <RiFileTextLine size={22} className="text-primary" />
                        </div>
                      ),
                      onClick: () => {
                        setServicePlanId(String(x.id));
                        setHasContract(true);
                      },
                      disabled: exists,
                    };
                  }
                  return {
                    label: x.name,
                    onClick: () => {
                      setServicePlanId(String(x.id));
                    },
                    disabled: exists,
                  };
                }
              ),
            },
          ]}
        />
      </div>
      {/* Property Id Dropdown */}
      <div className="mb-3">
        <div className="text-gray-700 mb-0.5">
          {tr("Select a property for this plan to apply to")}
        </div>
        <Dropdown
          reference={
            <button className="h-10 w-full bg-white border border-gray-300 rounded-md shadow-sm text-gray-700 text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 px-2">
              {propertyId ? (
                <div className="relative">
                  <div>
                    {properties.data?.find(
                      (x: { id: { toString: () => any } }) => {
                        if (x.id?.toString() === propertyId?.toString()) {
                          return true;
                        } else {
                          return false;
                        }
                      }
                    )?.address +
                      " " +
                      properties.data?.find(
                        (x: { id: { toString: () => any } }) => {
                          if (x.id?.toString() === propertyId?.toString()) {
                            return true;
                          } else {
                            return false;
                          }
                        }
                      )?.city +
                      ", " +
                      properties.data?.find(
                        (x: { id: { toString: () => any } }) => {
                          if (x.id?.toString() === propertyId?.toString()) {
                            return true;
                          } else {
                            return false;
                          }
                        }
                      )?.state +
                      " " +
                      properties.data?.find(
                        (x: { id: { toString: () => any } }) => {
                          if (x.id?.toString() === propertyId?.toString()) {
                            return true;
                          } else {
                            return false;
                          }
                        }
                      )?.zipcode}
                  </div>
                  <div className="absolute top-[-20px] right-[-20px] bg-white rounded-full border border-gray-300 p-0.5">
                    <FaCheck size={18} className="text-green-500" />
                  </div>
                </div>
              ) : (
                tr("Select Property")
              )}
            </button>
          }
          data={[
            {
              button: properties.data?.map((x: IProperty) => {
                const exists = isPropertyPlanExist(String(x.id));

                let name =
                  x.address + " " + x.city + ", " + x.state + " " + x.zipcode;
                return {
                  onClick: () => {
                    setPropertyId(x.id);
                  },
                  label: name,
                  value: x.id,
                  disabled: exists,
                };
              }),
            },
          ]}
        />
      </div>
      {/* Start Date */}
      <div className="mb-3">
        {servicePlanId && (
          <>
            <div className="text-gray-700 mb-0.5">
              {tr("Select a start date for this plan")}
            </div>
            <ToggleSwitch
              options={[
                { value: "immediately", label: tr("Immediately") },
                { value: "new_year", label: tr("January 1st"), tooltip: `${tr("January 1st")}, ${new Date().getFullYear()}` },
                { value: "custom", label: tr("Custom") },
              ]}
              defaultValue={dateOption}
              onChange={(value) => {
                setDateOption(value);

                if (value === "immediately") {
                  const immediately = new Date();
                  setStartDate(immediately.toISOString());
                } else if (value === "new_year") {
                  const currentYear = new Date();
                  currentYear.setFullYear(currentYear.getFullYear());
                  currentYear.setMonth(0);
                  currentYear.setDate(1);
                  setStartDate(currentYear.toISOString());
                } else if (value === "custom") {
                  // Don't reset the date here, just keep the last selected date
                  // This allows for better user experience when toggling back to custom
                }
              }}
            />
            {dateOption === "custom" && (
              <div className="mt-2">
                <DatePicker
                  className="h-10 w-full bg-white border border-gray-300 rounded-md shadow-sm text-gray-700 text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 px-2"
                  selected={new Date(startDate)}
                  onChange={(date) => {
                    if (date && date instanceof Date) {
                      // Set time to noon to avoid timezone issues
                      date.setHours(12, 0, 0, 0);
                      setStartDate(date.toISOString());
                    }
                  }}
                  popperPlacement="top"
                  locale={locale}
                />
              </div>
            )}
          </>
        )}
      </div>
      <div className="flex justify-between mt-6">
        <Button
          type="secondary"
          onClick={() => {
            setModal();
          }}
        >
          {tr("Cancel")}
        </Button>
        <Button
          style={{ float: "right" }}
          primary
          onClick={() => {
            if (hasContract === true) {
              setModal({
                label: tr("Send Contract"),
                component: (
                  <ConfirmSendModal
                    servicePlanId={servicePlanId}
                    propertyId={propertyId}
                    customerEmail={customerEmail}
                    customerId={customerId}
                    type="send"
                    startDate={startDate}
                  />
                ),
              });
            } else {
              addPlanToCustomer.mutateAsync({
                plans: [
                  {
                    servicePlanId,
                    customerId,
                    propertyId,
                    startDate,
                  },
                ],
              });
              setModal();
            }
          }}
          disabled={!servicePlanId || !propertyId}
        >
          {tr("Confirm")}
        </Button>
      </div>
    </div>
  );
};

export default AddPlanModal;